import React, { useEffect, useMemo, useRef, useState } from 'react';
import { crearClasificacion, fetchCasosPorIds, fetchClasificacion, fetchDataset, listarMotivos } from './api';
import { refrescarCasos } from './firmas';
import FormularioCorrida from './FormularioCorrida';
import { formularioDePlantilla, sanearFormulario } from './formulario';
import { separarPorTipo, universoLocal } from './metricas';
import { clasificacionTerminada, mezclarMotivos } from './motivos';
import PanelPreseleccion from './PanelPreseleccion';
import { borrarEstadoCasos, guardarEstadoCasos, leerEstadoCasos, storageDelNavegador, type EstadoCasosGuardado } from './persistencia';
import SeccionCasos, { Campo } from './SeccionCasos';
import type { ClasificacionMotivos, ConfigAnalisis, ConfigFormulario, Dataset, ParamsDataset, PlantillaCorrida } from './tipos';
import { Aviso, btnPrimario, btnSecundario, inputChicoCls } from './ui';
import { useRefrescoFirmas } from './useRefrescoFirmas';

const PARAMS_DEFAULT: ParamsDataset = {
  rubro: 'Moda e Indumentaria',
  desde: '2026-07-15',
  prioridad_max: 3,
  por_empresa: 4,
  limit: 60,
  control: 15,
};

type Restauracion = { estado: 'nada' | 'cargando' | 'error'; guardado: EstadoCasosGuardado | null; error: string | null };
const SIN_RESTAURAR: Restauracion = { estado: 'nada', guardado: null, error: null };

/**
 * Sub-vista Casos: filtros → dataset (editadas + control) → selección → lanzar corrida.
 * Al cargar quedan TODOS seleccionados. Tres orígenes de la tanda: el dataset del backend,
 * una plantilla de "Reutilizar configuración" (casos de una corrida) o lo guardado en
 * localStorage (ids → POST /casos con URLs frescas). Todo cambio se persiste con debounce,
 * recién después de que la restauración inicial terminó (`listo`): si no, el primer render
 * vacío pisaría lo guardado.
 *
 * Preselección con IA: `POST /clasificaciones` clasifica el motivo de las ediciones y se
 * pollea cada 3 s; cada vez que avanza se traen los motivos (`POST /motivos/listar`) y se
 * mezclan en `dataset.casos`, así las tarjetas se pintan a medida que terminan las tandas.
 * Cargar otro dataset, aplicar plantilla o descartar resetea la clasificación a null: el
 * poll se corta solo y la generación evita que una respuesta tardía pise la tanda nueva.
 */
const AnalisisCasos: React.FC<{
  config: ConfigAnalisis;
  onIrACorrida: (runId: string) => void;
  plantilla: { plantilla: PlantillaCorrida; n: number } | null;
}> = ({ config, onIrACorrida, plantilla }) => {
  const storage = useMemo(storageDelNavegador, []);
  const [params, setParams] = useState<ParamsDataset>(PARAMS_DEFAULT);
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selEditadas, setSelEditadas] = useState<Set<string>>(new Set());
  const [selControl, setSelControl] = useState<Set<string>>(new Set());
  /** Etiqueta de la corrida de la que vienen los casos (nota "Casos de la corrida X"). */
  const [origen, setOrigen] = useState<string | null>(null);
  // El formulario se resetea cambiando su key; `formCfg` es lo último que reportó (se persiste).
  const [formInicial, setFormInicial] = useState<ConfigFormulario | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [formCfg, setFormCfg] = useState<ConfigFormulario | null>(null);
  const [restauracion, setRestauracion] = useState<Restauracion>(SIN_RESTAURAR);
  const [listo, setListo] = useState(false);
  // Generación de la tanda: una restauración en vuelo que resuelve DESPUÉS de que el
  // usuario cargó otro dataset, aplicó una plantilla o descartó, no pisa lo nuevo.
  const generacionRef = useRef(0);
  // Clasificación con IA del motivo de las ediciones: el job en curso (poll) y su error.
  const [clasificacion, setClasificacion] = useState<ClasificacionMotivos | null>(null);
  const [modeloClasif, setModeloClasif] = useState(config.modelos_clasificacion[0] ?? '');
  const [lanzandoClasif, setLanzandoClasif] = useState(false);
  const [errorClasif, setErrorClasif] = useState<string | null>(null);
  const [tickClasif, setTickClasif] = useState(0);

  const aplicarFormulario = (cfg: ConfigFormulario | null) => {
    setFormInicial(cfg);
    setFormCfg(cfg);
    setFormKey((k) => k + 1);
  };

  const restaurar = async (g: EstadoCasosGuardado) => {
    const generacion = ++generacionRef.current;
    setRestauracion({ estado: 'cargando', guardado: g, error: null });
    try {
      const todos = await fetchCasosPorIds(g.imagen_ids, g.control_ids);
      if (generacionRef.current !== generacion) return;
      const { casos, control } = separarPorTipo(todos);
      const idsE = new Set(casos.map((c) => c.imagen_id));
      const idsC = new Set(control.map((c) => c.imagen_id));
      setDataset({ universo: g.universo ?? universoLocal(todos), casos, control });
      setSelEditadas(new Set(g.sel_editadas.filter((id) => idsE.has(id))));
      setSelControl(new Set(g.sel_control.filter((id) => idsC.has(id))));
      setRestauracion(SIN_RESTAURAR);
      setListo(true);
    } catch (e) {
      console.error('[analisis] restaurar casos guardados falló:', e);
      if (generacionRef.current !== generacion) return;
      setRestauracion({ estado: 'error', guardado: g, error: e instanceof Error ? e.message : String(e) });
    }
  };

  // Al montar: lo guardado (si hay) manda; los params y el formulario vuelven aunque no haya ids.
  useEffect(() => {
    const g = leerEstadoCasos(storage);
    if (!g) {
      setListo(true);
      return;
    }
    setParams(g.params);
    setOrigen(g.origen);
    if (g.formulario) aplicarFormulario(sanearFormulario(g.formulario, config));
    if (g.imagen_ids.length + g.control_ids.length === 0) {
      setListo(true);
      return;
    }
    void restaurar(g);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Plantilla de "Reutilizar configuración": la tanda de esa corrida, todo seleccionado,
  // formulario prellenado. Pisa lo que hubiera (dataset o restauración a medias).
  useEffect(() => {
    if (!plantilla) return;
    generacionRef.current += 1;
    const p = plantilla.plantilla;
    const { casos, control } = separarPorTipo(p.casos);
    setDataset({ universo: universoLocal(p.casos), casos, control });
    setSelEditadas(new Set(casos.map((c) => c.imagen_id)));
    setSelControl(new Set(control.map((c) => c.imagen_id)));
    setOrigen(p.origen.etiqueta);
    aplicarFormulario(formularioDePlantilla(p, config));
    setError(null);
    setClasificacion(null);
    setErrorClasif(null);
    setRestauracion(SIN_RESTAURAR);
    setListo(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantilla]);

  // Persistencia con debounce: ids (no tarjetas), selecciones, params y formulario.
  useEffect(() => {
    if (!listo) return;
    const timer = setTimeout(
      () =>
        guardarEstadoCasos(storage, {
          params,
          universo: dataset?.universo ?? null,
          imagen_ids: dataset?.casos.map((c) => c.imagen_id) ?? [],
          control_ids: dataset?.control.map((c) => c.imagen_id) ?? [],
          sel_editadas: [...selEditadas],
          sel_control: [...selControl],
          formulario: formCfg,
          origen,
        }),
      400,
    );
    return () => clearTimeout(timer);
  }, [listo, storage, params, dataset, selEditadas, selControl, formCfg, origen]);

  const cargar = async () => {
    const generacion = ++generacionRef.current;
    setCargando(true);
    setError(null);
    setClasificacion(null);
    setErrorClasif(null);
    try {
      const d = await fetchDataset(params);
      if (generacionRef.current !== generacion) return;
      setDataset(d);
      setSelEditadas(new Set(d.casos.map((c) => c.imagen_id)));
      setSelControl(new Set(d.control.map((c) => c.imagen_id)));
      setOrigen(null);
      setRestauracion(SIN_RESTAURAR);
      setListo(true);
    } catch (e) {
      console.error('[analisis] dataset falló:', e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCargando(false);
    }
  };

  const descartar = () => {
    generacionRef.current += 1;
    borrarEstadoCasos(storage);
    setParams(PARAMS_DEFAULT);
    setDataset(null);
    setSelEditadas(new Set());
    setSelControl(new Set());
    setOrigen(null);
    aplicarFormulario(null);
    setError(null);
    setClasificacion(null);
    setErrorClasif(null);
    setRestauracion(SIN_RESTAURAR);
    setListo(true);
  };

  /** Trae los motivos cacheados de esas imágenes y los pinta en el dataset, si la tanda sigue siendo la misma. */
  const mezclarDesdeBackend = async (generacion: number, ids: string[]) => {
    try {
      const motivos = await listarMotivos(ids);
      if (generacionRef.current !== generacion) return;
      setDataset((d) => d && { ...d, casos: mezclarMotivos(d.casos, motivos) });
    } catch (e) {
      console.error('[analisis] listar motivos falló:', e);
      if (generacionRef.current === generacion) setErrorClasif(`No se pudieron traer los motivos: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const clasificar = async (forzar: boolean) => {
    if (!dataset || dataset.casos.length === 0) return;
    const generacion = generacionRef.current;
    // Se mandan TODAS las editadas: el backend descarta las que ya tienen motivo (salvo forzar).
    const ids = dataset.casos.map((c) => c.imagen_id);
    setLanzandoClasif(true);
    setErrorClasif(null);
    try {
      const creada = await crearClasificacion(ids, modeloClasif || undefined, forzar);
      if (generacionRef.current !== generacion) return;
      if (creada.clasificacion_id === null) {
        // Nada pendiente (ya estaban cacheadas, quizá desde otra sesión): solo traer lo que hay.
        await mezclarDesdeBackend(generacion, ids);
        return;
      }
      const c = await fetchClasificacion(creada.clasificacion_id);
      if (generacionRef.current !== generacion) return;
      setClasificacion(c);
    } catch (e) {
      console.error('[analisis] clasificar ediciones falló:', e);
      if (generacionRef.current === generacion) setErrorClasif(e instanceof Error ? e.message : String(e));
    } finally {
      setLanzandoClasif(false);
    }
  };

  // Poll de la clasificación en curso cada 3 s hasta `finished_at` (mismo patrón que Comparar).
  useEffect(() => {
    if (!clasificacion || clasificacionTerminada(clasificacion)) return;
    const generacion = generacionRef.current;
    const timer = setTimeout(() => {
      fetchClasificacion(clasificacion.id)
        .then((c) => {
          if (generacionRef.current !== generacion) return;
          setClasificacion((prev) => (prev && prev.id === c.id ? c : prev));
          // Un poll que se recupera limpia SOLO su propio aviso; un error de listarMotivos queda hasta la próxima mezcla.
          setErrorClasif((prev) => (prev && prev.startsWith('El poll falló') ? null : prev));
        })
        .catch((e) => {
          console.error('[analisis] poll de clasificación falló:', e);
          if (generacionRef.current === generacion) setErrorClasif(`El poll falló (se reintenta en 3 s): ${e instanceof Error ? e.message : String(e)}`);
        })
        .finally(() => setTickClasif((t) => t + 1));
    }, 3000);
    return () => clearTimeout(timer);
  }, [clasificacion, tickClasif]);

  // Cada vez que la clasificación avanza (`hechas`: una tanda de 40 terminó) o termina, se
  // traen los motivos de las editadas y se pintan. Recién creada (hechas 0, sin terminar)
  // no hay nada nuevo que traer.
  const idClasif = clasificacion?.id ?? null;
  const hechasClasif = clasificacion?.hechas ?? 0;
  const terminoClasif = clasificacion !== null && clasificacionTerminada(clasificacion);
  useEffect(() => {
    if (!idClasif || !dataset || (hechasClasif === 0 && !terminoClasif)) return;
    void mezclarDesdeBackend(
      generacionRef.current,
      dataset.casos.map((c) => c.imagen_id),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idClasif, hechasClasif, terminoClasif]);

  // El error del job (p. ej. "1 de 8 tandas fallaron") se muestra al terminar sin perder lo clasificado.
  const errorPreseleccion = errorClasif ?? (terminoClasif ? clasificacion?.error ?? null : null);

  // URLs firmadas (1 h): el dataset se carga una vez y se mira mucho más de una hora. Cuando
  // están por vencer se re-piden los casos por id (mismo camino que la restauración) y se
  // reemplazan POR ID: universo, selección, formulario y motivo quedan como están. Si la
  // tanda cambió mientras tanto (generación), se descarta.
  const casosParaFirmas = useMemo(() => (dataset ? [...dataset.casos, ...dataset.control] : []), [dataset]);
  const refrescarFirmas = async () => {
    if (!dataset) return;
    const generacion = generacionRef.current;
    const frescos = await fetchCasosPorIds(
      dataset.casos.map((c) => c.imagen_id),
      dataset.control.map((c) => c.imagen_id),
    );
    if (generacionRef.current !== generacion) return;
    const { casos, control } = separarPorTipo(frescos);
    setDataset((d) => d && { ...d, casos: refrescarCasos(d.casos, casos), control: refrescarCasos(d.control, control) });
  };
  useRefrescoFirmas(casosParaFirmas, refrescarFirmas);

  const set = (k: keyof ParamsDataset, v: string) =>
    setParams((p) => ({ ...p, [k]: k === 'rubro' || k === 'desde' ? v : Number(v) }));

  const hayGuardado = dataset !== null || restauracion.estado !== 'nada' || origen !== null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-4">
        <Campo label="Rubro">
          <input value={params.rubro} onChange={(e) => set('rubro', e.target.value)} className={`${inputChicoCls} w-48`} />
        </Campo>
        <Campo label="Desde">
          <input type="date" value={params.desde} onChange={(e) => set('desde', e.target.value)} className={inputChicoCls} />
        </Campo>
        <Campo label="Prioridad máx.">
          <select value={params.prioridad_max} onChange={(e) => set('prioridad_max', e.target.value)} className={inputChicoCls}>
            <option value={1}>1 (solo producto)</option>
            <option value={2}>2</option>
            <option value={3}>3 (todas)</option>
          </select>
        </Campo>
        <Campo label="Máx. por empresa">
          <input type="number" min={1} value={params.por_empresa} onChange={(e) => set('por_empresa', e.target.value)} className={`${inputChicoCls} w-20`} />
        </Campo>
        <Campo label="Límite">
          <input type="number" min={1} max={500} value={params.limit} onChange={(e) => set('limit', e.target.value)} className={`${inputChicoCls} w-20`} />
        </Campo>
        <Campo label="Control">
          <input type="number" min={0} max={500} value={params.control} onChange={(e) => set('control', e.target.value)} className={`${inputChicoCls} w-20`} />
        </Campo>
        <button type="button" onClick={() => void cargar()} disabled={cargando || restauracion.estado === 'cargando'} className={btnPrimario}>
          {cargando ? 'Cargando…' : 'Cargar casos'}
        </button>
        {hayGuardado && (
          <button type="button" onClick={descartar} className={`${btnSecundario} py-1.5 px-3 text-xs`} title="Borra lo guardado en este navegador y vuelve a los defaults">
            Descartar guardado
          </button>
        )}
        {dataset && (
          <span className="text-xs text-gray-500">
            Universo: <b className="text-brand-black">{dataset.universo.total}</b> imágenes editadas de{' '}
            <b className="text-brand-black">{dataset.universo.empresas}</b> empresas · P1 {dataset.universo.prio1} · P2 {dataset.universo.prio2} · P3{' '}
            {dataset.universo.prio3}
          </span>
        )}
      </div>
      <Aviso error={error} />

      {restauracion.estado === 'cargando' && (
        <p className="animate-pulse text-sm text-gray-500">
          Restaurando la selección guardada… ({restauracion.guardado?.imagen_ids.length ?? 0} editadas + {restauracion.guardado?.control_ids.length ?? 0} control)
        </p>
      )}
      {restauracion.estado === 'error' && (
        <div className="space-y-2">
          <Aviso error={`No se pudo restaurar la selección guardada: ${restauracion.error ?? ''}`} />
          <button type="button" onClick={() => restauracion.guardado && void restaurar(restauracion.guardado)} className={`${btnSecundario} py-1.5 px-3 text-xs`}>
            Reintentar
          </button>
        </div>
      )}

      {dataset && (
        <>
          {origen && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 py-2 px-4 text-sm text-amber-800">
              Casos de la corrida <b>{origen}</b> — misma tanda; ajustá modelo, effort o prompt y volvé a analizar.
            </p>
          )}
          {dataset.casos.length > 0 && (
            <PanelPreseleccion
              modelos={config.modelos_clasificacion}
              modelo={modeloClasif}
              onModelo={setModeloClasif}
              casos={dataset.casos}
              clasificacion={clasificacion}
              lanzando={lanzandoClasif}
              error={errorPreseleccion}
              onClasificar={(forzar) => void clasificar(forzar)}
              onSeleccionar={(ids) => setSelEditadas(new Set(ids))}
            />
          )}
          <FormularioCorrida
            key={formKey}
            config={config}
            imagenIds={[...selEditadas]}
            controlIds={[...selControl]}
            onIrACorrida={onIrACorrida}
            inicial={formInicial}
            onChange={setFormCfg}
          />
          <SeccionCasos titulo="Editadas (el cliente las corrigió → rehacer esperado)" casos={dataset.casos} sel={selEditadas} onSel={setSelEditadas} filtros />
          <SeccionCasos titulo="Control (descargadas y nunca editadas → rehacer = falso positivo)" casos={dataset.control} sel={selControl} onSel={setSelControl} />
        </>
      )}
      {!dataset && !cargando && !error && restauracion.estado === 'nada' && (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-500">
          Ajustá los filtros y tocá <b>Cargar casos</b>.
        </div>
      )}
    </div>
  );
};

export default AnalisisCasos;
